import PropTypes from 'prop-types'

import AtomCard, {atomCardCornerSize, atomCardElevation} from '@s-ui/react-atom-card-v2'

import CardRatingActions from './components/CardRatingActions.js'
import CardRatingBody from './components/CardRatingBody.js'
import CardRatingCategories from './components/CardRatingCategories.js'
import CardRatingHeader from './components/CardRatingHeader.js'
import CardRatingReply from './components/CardRatingReply.js'
import CardRatingReplyForm from './components/CardRatingReplyForm.js'
import CardRatingTags from './components/CardRatingTags.js'
import {
  DEFAULT_REPLY_BUTTON_LABEL,
  DEFAULT_REPLY_FORM_CANCEL_BUTTON_LABEL,
  DEFAULT_REPLY_FORM_HELP_TEXT,
  DEFAULT_REPLY_FORM_PLACEHOLDER,
  DEFAULT_REPLY_FORM_SUBMIT_BUTTON_LABEL,
  DEFAULT_REPORT_BUTTON_LABEL,
  NO_OP
} from './config.js'
import {useFeedbackActions} from './useFeedbackActions.js'

const CardRating = ({
  id,
  isOwner = false,
  onReply = NO_OP,
  onReport = NO_OP,
  rating,
  ratingTargetName,
  replyButtonLabel = DEFAULT_REPLY_BUTTON_LABEL,
  reportButtonLabel = DEFAULT_REPORT_BUTTON_LABEL,
  replyCancelButtonLabel = DEFAULT_REPLY_FORM_CANCEL_BUTTON_LABEL,
  replyDate,
  replyFormHelpText = DEFAULT_REPLY_FORM_HELP_TEXT,
  replyFormPlaceholder = DEFAULT_REPLY_FORM_PLACEHOLDER,
  replyMessage,
  replySubmitButtonLabel = DEFAULT_REPLY_FORM_SUBMIT_BUTTON_LABEL,
  reviewDate,
  reviewerName,
  reviewText,
  showReplyAction = false,
  showReportAction = false,
  tags = [],
  categories = []
}) => {
  const {
    isReplySubmitDisabled,
    showReplyForm,
    replyText,
    handleReplyTextChange,
    handleReplySubmit,
    handleReplyCancel,
    handleReplyClick,
    handleReportClick
  } = useFeedbackActions({onReply, onReport, id})

  const hasActions = showReplyAction || showReportAction
  const hasBody = Boolean(reviewText)
  const hasCategories = Boolean(categories.length)
  const hasReplyMessage = Boolean(replyMessage)
  const hasReviewerName = Boolean(reviewerName)
  const hasTags = Boolean(tags.length)

  return (
    <AtomCard cornerSize={atomCardCornerSize.L} elevation={atomCardElevation.XS} isInset>
      <div className="mt-CardRating" data-testid="card-rating">
        <div className="mt-CardRating-section">
          <CardRatingHeader
            rating={rating}
            reviewerName={reviewerName}
            reviewDate={reviewDate}
            showRating={!hasCategories}
            actions={
              hasActions && (
                <CardRatingActions
                  onClickReplyAction={handleReplyClick}
                  onClickReportAction={handleReportClick}
                  replyActionDisabled={hasReplyMessage}
                  replyActionSelected={showReplyForm}
                  replyButtonLabel={replyButtonLabel}
                  reportButtonLabel={reportButtonLabel}
                />
              )
            }
          >
            <CardRatingCategories categories={categories} />
          </CardRatingHeader>
          {hasCategories && hasReviewerName && <CardRatingCategories categories={categories} />}
          {hasBody && <CardRatingBody message={reviewText} />}
          {hasTags && <CardRatingTags tags={tags} />}
          {hasReplyMessage && (
            <CardRatingReply
              isOwner={isOwner}
              message={replyMessage}
              ratingTargetName={ratingTargetName}
              replyDate={replyDate}
            />
          )}
        </div>
      </div>
      {showReplyForm && (
        <>
          <div className="mt-CardRating-divider" />
          <div className="mt-CardRating-section">
            <CardRatingReplyForm
              helpText={replyFormHelpText}
              id={id}
              onChange={handleReplyTextChange}
              onReplySubmit={handleReplySubmit}
              onReplyCancel={handleReplyCancel}
              placeholder={replyFormPlaceholder}
              replyCancelButtonLabel={replyCancelButtonLabel}
              replySubmitButtonLabel={replySubmitButtonLabel}
              text={replyText}
              isReplySubmitDisabled={isReplySubmitDisabled}
            />
          </div>
        </>
      )}
    </AtomCard>
  )
}

CardRating.displayName = 'CardRating'

CardRating.propTypes = {
  id: PropTypes.string.isRequired,
  isOwner: PropTypes.bool,
  onReply: PropTypes.func,
  onReport: PropTypes.func,
  rating: PropTypes.number,
  ratingTargetName: PropTypes.string,
  replyButtonLabel: PropTypes.string,
  replyCancelButtonLabel: PropTypes.string,
  replyDate: PropTypes.string,
  replyFormHelpText: PropTypes.string,
  replyFormPlaceholder: PropTypes.string,
  replyMessage: PropTypes.string,
  replySubmitButtonLabel: PropTypes.string,
  reportButtonLabel: PropTypes.string,
  reviewDate: PropTypes.string.isRequired,
  reviewerName: PropTypes.string.isRequired,
  reviewText: PropTypes.string,
  showReplyAction: PropTypes.bool,
  showReportAction: PropTypes.bool,
  tags: PropTypes.array,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      rating: PropTypes.number.isRequired
    })
  )
}

export default CardRating
